import { localeTag, mediaSrc, type Condition, type Definition, type PublicSurvey, type Question, type Submission } from '@survey/core';

export interface LibraryQuestion {
  id: string;
  ownerId: string;
  ownerName: string;
  question: Question;
  createdAt: string;
}

export interface FileAnswer {
  url: string;
  name: string;
  size: number;
  type?: string;
}
import { getLocale, t } from '../i18n';
import { cacheSurvey, getCachedSurvey } from '../offline';
import { turnstileToken } from '../turnstile';
import type {
  Collaborator,
  ResponseRecord,
  Role,
  RoleDef,
  RolesResponse,
  Session,
  SurveyDetail,
  SurveyReport,
  SurveyStatus,
  SurveySummary,
  SurveyVersion,
  User,
  RespondentRecord,
  Enumerator,
  AnalysisSettings,
  AnalysisVariable,
  AuditPage,
  Crosstab,
  Invitation,
  InvitationSurvey,
  Collection,
  CollectionSettings,
  SurveyInvite,
  InviteStats,
  SurveyProgress,
} from './types';

const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');
const TOKEN_KEY = 'survey.token';

/** Error thrown for any non-2xx API response, carrying the server's error envelope. */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fields: Record<string, string> = {},
    public details?: unknown,
  ) {
    super(message);
  }
}

function readToken(): string | null {
  try {
    // "Remember me" keeps the token in localStorage; otherwise it lives in sessionStorage
    // and is gone when the browser closes.
    return localStorage.getItem(TOKEN_KEY) ?? sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

let token: string | null = readToken();
const listeners = new Set<() => void>();

export function setToken(t: string | null, remember = true) {
  token = t;
  try {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    if (t) (remember ? localStorage : sessionStorage).setItem(TOKEN_KEY, t);
  } catch {
    // storage unavailable (private mode); keep the token in memory only
  }
  window.dispatchEvent(new Event('yellowmetrics-auth-change'));
}

export function getToken() {
  return token;
}

/** Decodes only the signed token subject for local cache partitioning; the API remains authoritative. */
export function getTokenSubject(): string | null {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
    const sub = (JSON.parse(atob(base64)) as { sub?: unknown }).sub;
    return typeof sub === 'string' ? sub : null;
  } catch {
    return null;
  }
}

/** Called when the server rejects our token so the app can return to sign-in. */
export function onUnauthorized(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: { raw?: boolean; headers?: Record<string, string>; signal?: AbortSignal } = {},
): Promise<T> {
  const headers: Record<string, string> = { ...opts.headers };
  const form = body instanceof FormData;
  if (body !== undefined && !form) headers['Content-Type'] = 'application/json';
  // The API answers in the user's language (English or Bahasa Indonesia).
  headers['Accept-Language'] = localeTag(getLocale());
  if (token) headers.Authorization = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch(BASE + path, { method, headers, signal: opts.signal, body: body === undefined ? undefined : form ? body : JSON.stringify(body) });
  } catch (e) {
    if (opts.signal?.aborted) throw e;
    throw new ApiError(0, 'network', t('error.network'));
  }
  if (!res.ok) {
    let payload: { error?: { code?: string; message?: string; fields?: Record<string, string>; details?: unknown } } = {};
    try {
      payload = await res.json();
    } catch {
      // non-JSON error body
    }
    const e = payload.error ?? {};
    if (res.status === 401 && token && (e.code === 'invalid_token' || e.code === 'unauthorized')) {
      setToken(null);
      listeners.forEach((fn) => fn());
    }
    throw new ApiError(res.status, e.code ?? 'error', e.message ?? t('error.request_failed', { status: res.status }), e.fields ?? {}, e.details);
  }
  if (opts.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Uploads one file as multipart "file", reporting progress (0–1). fetch cannot report upload
 * progress, so this uses XMLHttpRequest; errors come back as ApiError like request().
 */
function uploadWithProgress<T>(path: string, file: File, onProgress?: (fraction: number) => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', BASE + path);
    xhr.setRequestHeader('Accept-Language', localeTag(getLocale()));
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new ApiError(0, 'network', t('error.network')));
    xhr.ontimeout = xhr.onerror;
    xhr.onload = () => {
      let payload: { error?: { code?: string; message?: string; fields?: Record<string, string>; details?: unknown } } & Record<string, unknown> = {};
      try {
        payload = JSON.parse(xhr.responseText || '{}');
      } catch {
        // non-JSON body, e.g. a proxy's "413 Request Entity Too Large" page
      }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(payload as T);
      if (xhr.status === 413) return reject(new ApiError(413, 'too_large', t('error.upload_too_large')));
      const e = payload.error ?? {};
      reject(new ApiError(xhr.status, e.code ?? 'error', e.message ?? t('error.request_failed', { status: xhr.status }), e.fields ?? {}, e.details));
    };
    const body = new FormData();
    body.append('file', file);
    xhr.send(body);
  });
}

type PublicConfig = { allowSignup: boolean; env: string; turnstileSiteKey?: string; assistant?: boolean; assistantOff?: boolean; assistantMock?: boolean };

/** Workspace-wide switches on the admin Settings page. */
export interface AppSettings {
  assistantEnabled: boolean;
}
export interface SettingsResponse {
  settings: AppSettings;
  /** What the server itself provides. */
  server: { assistantConfigured: boolean; assistantMock: boolean };
}

/** Fired after an administrator saved settings, so open views refresh what they show. */
export const SETTINGS_UPDATED_EVENT = 'app-settings-updated';
let configLoad: Promise<PublicConfig> | null = null;

/** The server's Turnstile site key, or undefined when submissions don't need a captcha. */
async function captchaSiteKey(): Promise<string | undefined> {
  configLoad ??= request<PublicConfig>('GET', '/api/config');
  try {
    return (await configLoad).turnstileSiteKey || undefined;
  } catch (e) {
    configLoad = null;
    throw e;
  }
}

/** Turnstile header for a submission nobody is watching (offline-queue retries). */
async function captchaHeaders(): Promise<Record<string, string>> {
  const siteKey = await captchaSiteKey();
  if (!siteKey) return {};
  try {
    return { 'X-Turnstile-Token': await turnstileToken(siteKey, getLocale()) };
  } catch {
    throw new ApiError(403, 'captcha_failed', t('captcha.failed'));
  }
}

const q = (params: Record<string, string | number | undefined>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') s.set(k, String(v));
  const str = s.toString();
  return str ? `?${str}` : '';
};

/** Response filter shared by the dashboard, cross-tabs and exports. */
export interface ResponseFilter {
  from?: string;
  to?: string;
  source?: '' | 'web' | 'mobile' | 'test';
  approval?: '' | 'pending' | 'approved' | 'rejected';
  includeTest?: boolean;
  match?: 'all' | 'any';
  conditions?: Condition[];
  weight?: boolean;
}

function filterQuery(f: ResponseFilter, extra: Record<string, string> = {}) {
  return q({
    ...extra,
    from: f.from,
    to: f.to,
    source: f.source,
    approval: f.approval,
    test: f.includeTest ? '1' : undefined,
    match: f.conditions?.length ? f.match : undefined,
    cond: f.conditions?.length ? JSON.stringify(f.conditions) : undefined,
    weight: f.weight ? '1' : undefined,
  });
}

/** A questionnaire drafted by the assistant. Nothing is saved until the user applies it. */
export interface AssistantProposal {
  kind: 'edit' | 'create';
  surveyId?: string;
  revision?: number;
  title: string;
  description: string;
  requireAuth?: boolean;
  definition: Definition;
  summary: string;
  added: number;
  updated?: number;
  removed?: number;
}

/** A chart the assistant's analysis tools asked the client to draw. */
export interface ChartSpec {
  kind: 'bar' | 'line';
  title: string;
  unit?: 'count' | 'percent' | 'mean';
  labels: string[];
  series: { name: string; values: number[] }[];
  note?: string;
}

/** What a number in an answer was computed from. */
export interface AssistantSource {
  surveyId: string;
  survey: string;
  responses: number;
  /** Human summary of the filters used, or ''. */
  filters: string;
  detail: string;
}

export type AssistantEvent =
  | { type: 'tool'; tool: string }
  | { type: 'proposal'; data: AssistantProposal }
  | { type: 'chart'; data: ChartSpec }
  | { type: 'source'; tool: string; data: AssistantSource }
  | { type: 'suggestions'; data: { items: string[] } }
  | { type: 'message' | 'error'; text: string }
  | { type: 'done' };

export interface AssistantMessage {
  role: 'user' | 'assistant';
  content: string;
}

/** One assistant turn. Events arrive as the server works (server-sent events over a POST). */
export async function assistantChat(
  body: { messages: AssistantMessage[]; surveyId?: string },
  onEvent: (e: AssistantEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await request<Response>('POST', '/api/assistant/chat', body, { raw: true, signal });
  const reader = res.body!.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    let end: number;
    while ((end = buffer.indexOf('\n\n')) >= 0) {
      const chunk = buffer.slice(0, end);
      buffer = buffer.slice(end + 2);
      for (const line of chunk.split('\n')) {
        if (line.startsWith('data: ')) onEvent(JSON.parse(line.slice(6)) as AssistantEvent);
      }
    }
  }
}

/** Fired after the assistant saved a questionnaire, so an open builder reloads it. Detail: the survey id. */
export const SURVEY_UPDATED_EVENT = 'yellowmetrics-survey-updated';

/** Full URL for question media; uploaded files live on the API server. */
export function mediaUrl(url: string) {
  return mediaSrc(url, BASE);
}

export const api = {
  config: () => request<PublicConfig>('GET', '/api/config'),
  settings: () => request<SettingsResponse>('GET', '/api/settings'),
  updateSettings: (body: Partial<AppSettings>) => request<SettingsResponse>('PATCH', '/api/settings', body),
  login: (email: string, password: string) => request<Session>('POST', '/api/auth/login', { email, password }),
  register: (email: string, name: string, password: string) =>
    request<Session>('POST', '/api/auth/register', { email, name, password }),
  registerInvited: (email: string, name: string, password: string, invite: string) =>
    request<{ pending: boolean; user: User }>('POST', '/api/auth/register', { email, name, password, invite }),
  publicInvitation: (token: string) => request<{ role: Role; roleName: string; label: string }>('GET', `/api/public/invitations/${encodeURIComponent(token)}`),
  me: () => request<User>('GET', '/api/auth/me'),
  updateMe: (body: { name?: string; title?: string; bio?: string; cover?: string; accentLight?: string; accentDark?: string; currentPassword?: string; newPassword?: string }) =>
    request<User>('PATCH', '/api/auth/me', body),
  uploadAvatar: (file: Blob) => {
    const body = new FormData();
    body.append('file', file, file.type === 'image/webp' ? 'avatar.webp' : 'avatar.jpg');
    return request<User>('POST', '/api/auth/me/avatar', body);
  },
  deleteAvatar: () => request<User>('DELETE', '/api/auth/me/avatar'),

  listUsers: (query?: string) => request<{ users: User[] }>('GET', '/api/users' + q({ q: query })),
  createUser: (body: { email: string; name: string; password: string; role: Role }) =>
    request<User>('POST', '/api/users', body),
  updateUser: (id: string, body: Partial<{ name: string; role: Role; active: boolean; password: string }>) =>
    request<User>('PATCH', `/api/users/${id}`, body),
  listInvitations: () => request<{ invitations: Invitation[] }>('GET', '/api/invitations'),
  createInvitation: (body: { label: string; role: Role; maxUses: number | null; expiresAt: string | null; surveys: InvitationSurvey[] }) =>
    request<Invitation>('POST', '/api/invitations', body),
  /** maxUses 0 removes the limit; clearExpiry removes the expiry. */
  updateInvitation: (
    id: string,
    body: Partial<{ label: string; maxUses: number; revoked: boolean; expiresAt: string; clearExpiry: boolean; surveys: InvitationSurvey[] }>,
  ) =>
    request<Invitation>('PATCH', `/api/invitations/${id}`, body),
  invitationSignups: (id: string) => request<{ users: User[] }>('GET', `/api/invitations/${id}/signups`),
  /** Leave out userIds to decide every pending sign-up from the link. */
  decideSignups: (id: string, decision: 'approve' | 'reject', userIds?: string[]) =>
    request<{ users: User[] }>('POST', `/api/invitations/${id}/${decision}`, userIds ? { userIds } : {}),

  roles: () => request<RolesResponse>('GET', '/api/roles'),
  createRole: (body: Pick<RoleDef, 'key' | 'name' | 'description' | 'permissions' | 'allSurveys'>) => request<RoleDef>('POST', '/api/roles', body),
  updateRole: (key: string, body: Pick<RoleDef, 'name' | 'description' | 'permissions' | 'allSurveys'>) =>
    request<RoleDef>('PUT', `/api/roles/${encodeURIComponent(key)}`, body),
  deleteRole: (key: string) => request<void>('DELETE', `/api/roles/${encodeURIComponent(key)}`),

  respondents: (query?: string) => request<{ respondents: RespondentRecord[] }>('GET', '/api/respondents' + q({ q: query, limit: 500 })),
  createRespondent: (body: Pick<RespondentRecord, 'name' | 'externalId' | 'gender'>) =>
    request<RespondentRecord>('POST', '/api/respondents', body),
  updateRespondent: (id: string, body: Omit<RespondentRecord, 'id'>) =>
    request<RespondentRecord>('PATCH', `/api/respondents/${id}`, body),
  enumerators: () => request<{ enumerators: Enumerator[] }>('GET', '/api/enumerators'),
  surveyEnumerators: (id: string) => request<{ enumerators: Enumerator[] }>('GET', `/api/surveys/${id}/enumerators`),
  setSurveyEnumerators: (id: string, userIds: string[]) =>
    request<{ enumerators: Enumerator[] }>('PUT', `/api/surveys/${id}/enumerators`, { userIds }),
  surveyRespondentAssignments: (id: string) => request<{ assignments: { respondentId: string; enumeratorId: string }[] }>('GET', `/api/surveys/${id}/respondent-assignments`),
  setSurveyRespondentAssignments: (id: string, assignments: { respondentId: string; enumeratorId: string }[]) =>
    request<{ assignments: { respondentId: string; enumeratorId: string }[] }>('PUT', `/api/surveys/${id}/respondent-assignments`, { assignments }),
  surveyGroupAssignments: (id: string) => request<{ assignments: { respondentId: string; groupName: string; stratum: string }[] }>('GET', `/api/surveys/${id}/group-assignments`),
  randomizeSurveyGroups: (id: string, stratifyBy: 'none' | 'gender', groups: { name: string; quotas: Record<string, number> }[]) =>
    request<{ assignments: { respondentId: string; groupName: string; stratum: string }[]; stratifyBy: 'none' | 'gender' }>('POST', `/api/surveys/${id}/group-assignments/randomize`, { stratifyBy, groups }),
  uploadMedia: (file: File, onProgress?: (fraction: number) => void) => uploadWithProgress<{ url: string; type: 'image' | 'video'; size: number }>('/api/media', file, onProgress),
  library: (search = '') => request<{ questions: LibraryQuestion[] }>('GET', '/api/library' + q({ q: search })),
  saveToLibrary: (question: Question) => request<LibraryQuestion>('POST', '/api/library', { question }),
  deleteFromLibrary: (id: string) => request<void>('DELETE', `/api/library/${id}`),
  surveyQuotas: (id: string) => request<{ counts: Record<string, number> }>('GET', `/api/surveys/${id}/quotas`),
  /** Stores a respondent's file for a file-upload question; the result is the answer value. */
  uploadRespondentFile: (code: string, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request<FileAnswer>('POST', `/api/public/surveys/${encodeURIComponent(code)}/uploads`, body);
  },
  /** Respondent uploads are private, so they are fetched with the session and shown from a blob URL. */
  respondentFileUrl: async (url: string) => {
    const res = await request<Response>('GET', url, undefined, { raw: true });
    return URL.createObjectURL(await res.blob());
  },
  /** Sets the custom public link /s/<slug>; an empty slug removes it. */
  setSlug: (id: string, slug: string) => request<{ slug: string }>('PUT', `/api/surveys/${id}/slug`, { slug }),
  approveSurvey: (id: string, status: 'approved' | 'rejected', note = '') => request<SurveyDetail>('POST', `/api/surveys/${id}/approval`, { status, note }),

  listSurveys: (params: { q?: string; status?: SurveyStatus }) =>
    request<{ surveys: SurveySummary[]; canCreate: boolean }>('GET', '/api/surveys' + q(params)),
  createSurvey: (body: { title: string; description?: string; definition?: Definition }) =>
    request<SurveyDetail>('POST', '/api/surveys', body),
  getSurvey: (id: string) => request<SurveyDetail>('GET', `/api/surveys/${id}`),
  saveSurvey: (
    id: string,
    body: { title: string; description: string; requireAuth: boolean; definition: Definition; revision: number },
  ) => request<SurveyDetail>('PUT', `/api/surveys/${id}`, body),
  deleteSurvey: (id: string) => request<void>('DELETE', `/api/surveys/${id}`),
  publish: (id: string) => request<SurveyDetail>('POST', `/api/surveys/${id}/publish`),
  close: (id: string) => request<SurveyDetail>('POST', `/api/surveys/${id}/close`),
  archive: (id: string) => request<SurveyDetail>('POST', `/api/surveys/${id}/archive`),
  restore: (id: string) => request<SurveyDetail>('POST', `/api/surveys/${id}/restore`),
  duplicate: (id: string) => request<SurveyDetail>('POST', `/api/surveys/${id}/duplicate`),
  collection: (id: string) => request<Collection>('GET', `/api/surveys/${id}/collection`),
  setCollection: (id: string, body: CollectionSettings) => request<Collection>('PUT', `/api/surveys/${id}/collection`, body),
  surveyInvites: (id: string) =>
    request<{ invites: SurveyInvite[]; stats: InviteStats; emailEnabled: boolean }>('GET', `/api/surveys/${id}/invites`),
  addSurveyInvites: (id: string, invitees: { email: string; name: string }[], send: boolean) =>
    request<{ added: number; skipped: number; invalid: string[] }>('POST', `/api/surveys/${id}/invites`, { invitees, send }),
  /** Leave out ids for every unanswered invitation. */
  sendSurveyInvites: (id: string, remind: boolean, ids?: string[]) =>
    request<{ queued: number }>('POST', `/api/surveys/${id}/invites/send`, ids ? { remind, ids } : { remind }),
  removeSurveyInvites: (id: string, ids: string[]) => request<{ removed: number }>('POST', `/api/surveys/${id}/invites/remove`, { ids }),
  progress: (id: string, days = 14) =>
    request<SurveyProgress>('GET', `/api/surveys/${id}/progress` + q({ tz: Intl.DateTimeFormat().resolvedOptions().timeZone, days: String(days) })),
  claimFlowPicks: (code: string, submissionId: string) => request<{ picks: Record<string, string[]> }>('POST', `/api/public/surveys/${encodeURIComponent(code)}/flow-picks`, { submissionId }),
  flowCounts: (id: string) => request<{ counts: Record<string, Record<string, number>> }>('GET', `/api/surveys/${id}/flow-counts`),
  versions: (id: string) => request<{ versions: SurveyVersion[] }>('GET', `/api/surveys/${id}/versions`),
  collaborators: (id: string) => request<{ collaborators: Collaborator[] }>('GET', `/api/surveys/${id}/collaborators`),
  addCollaborator: (id: string, email: string, permission: 'editor' | 'viewer') =>
    request<{ collaborators: Collaborator[] }>('PUT', `/api/surveys/${id}/collaborators`, { email, permission }),
  removeCollaborator: (id: string, userId: string) => request<void>('DELETE', `/api/surveys/${id}/collaborators/${userId}`),

  responses: (id: string, offset: number, limit: number) =>
    request<{ responses: ResponseRecord[]; total: number; unapproved: number }>('GET', `/api/surveys/${id}/responses` + q({ offset, limit })),
  approveResponse: (id: string, responseId: string, status: 'approved' | 'rejected', note = '') =>
    request<ResponseRecord>('POST', `/api/surveys/${id}/responses/${responseId}/approval`, { status, note }),
  summary: (id: string, filter: ResponseFilter = {}) => request<SurveyReport>('GET', `/api/surveys/${id}/summary` + filterQuery(filter)),
  variables: (id: string) => request<{ variables: AnalysisVariable[] }>('GET', `/api/surveys/${id}/variables`),
  crosstab: (id: string, row: string, col: string, filter: ResponseFilter = {}) =>
    request<Crosstab>('GET', `/api/surveys/${id}/crosstab` + filterQuery(filter, { row, col })),
  analysisSettings: (id: string) => request<AnalysisSettings>('GET', `/api/surveys/${id}/analysis`),
  saveAnalysisSettings: (id: string, settings: AnalysisSettings) => request<AnalysisSettings>('PUT', `/api/surveys/${id}/analysis`, settings),
  updateResponse: (id: string, responseId: string, answers: Record<string, unknown>) =>
    request<ResponseRecord>('PUT', `/api/surveys/${id}/responses/${responseId}`, { answers }),
  createTestResponses: (id: string, count: number) => request<{ created: number }>('POST', `/api/surveys/${id}/test-responses`, { count }),
  deleteTestResponses: (id: string) => request<{ deleted: number }>('DELETE', `/api/surveys/${id}/test-responses`),
  surveyAudit: (id: string, params: { action?: string; offset?: number; limit?: number } = {}) =>
    request<AuditPage>('GET', `/api/surveys/${id}/audit` + q(params)),
  audit: (params: { action?: string; actor?: string; offset?: number; limit?: number } = {}) => request<AuditPage>('GET', '/api/audit' + q(params)),
  /** Downloads an export with the auth header and saves it via a temporary link. */
  async download(id: string, format: 'csv' | 'xlsx' | 'sav', filter: ResponseFilter = {}) {
    const res = await request<Response>('GET', `/api/surveys/${id}/export.${format}` + filterQuery(filter), undefined, { raw: true });
    const blob = await res.blob();
    const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? `responses.${format}`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return name;
  },

  /** invite is the personal invitation token from the link, if any. */
  async publicSurvey(code: string, invite?: string) {
    try {
      const survey = await request<PublicSurvey>('GET', `/api/public/surveys/${encodeURIComponent(code)}` + q({ invite }));
      await cacheSurvey(survey, getTokenSubject()).catch(() => undefined);
      return survey;
    } catch (error) {
      if (error instanceof ApiError && error.status === 0) {
        const cached = await getCachedSurvey(code, getTokenSubject());
        if (cached) return cached;
      }
      throw error;
    }
  },
  captchaSiteKey,
  /** Pass the token from the visible widget; without one, an invisible challenge runs. */
  submit: async (code: string, submission: Submission, captchaToken?: string) =>
    request<{ id: string; duplicate: boolean }>('POST', `/api/public/surveys/${encodeURIComponent(code)}/responses`, submission, {
      headers: captchaToken ? { 'X-Turnstile-Token': captchaToken } : await captchaHeaders(),
    }),
};
